/**
 * Add category (C8; TMMAPP-0108; audit 06 § C8, v2 L2293–2333, reference v2/11): "Add category",
 * "Categories belong to this virtual account. Every envelope's register can use them.", the
 * Direction radio cards Expense (↓) / Income (↑), Name, Short description, and the tax checkbox —
 * "Tax deductible" on an expense, "Taxable" on income (MM-D-127) — then Cancel / Add. It saves to
 * the virtual account (MM-D-124). The form is its own component so the Category / split dialog (C7)
 * can show it in place of its rows ("+ New category"), never as a second dialog (DLG-005).
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { categoriesApi, type Category, type CategoryKind } from '@/api/categories';
import { ErrorCodes, isApiError } from '@/api/errors';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { DirectionIcon } from '@/components/icons';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { EMPTY_CATEGORY_DRAFT, isCategoryDraftDirty, type CategoryDraft } from './categoryDraft';

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 255;
const KINDS: CategoryKind[] = ['EXPENSE', 'INCOME'];

/** A direction radio card (visual contract §14): `selected` fill and a `brandBoundary` border. */
function DirectionCard({
  kind,
  checked,
  name,
  onSelect,
}: {
  kind: CategoryKind;
  checked: boolean;
  name: string;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="label"
      data-checked={checked}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        p: '11px 12px',
        border: `1px solid ${checked ? tokens.brandBoundary : tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        bgcolor: tokens.surfaceRaised,
        backgroundImage: checked
          ? `linear-gradient(${tokens.selected}, ${tokens.selected})`
          : 'none',
        color: tokens.text,
        '&:has(input:focus-visible)': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        style={visuallyHidden}
      />
      <Box
        component="span"
        aria-hidden
        sx={{
          display: 'flex',
          color: checked ? tokens.link : tokens.text2,
          transform: kind === 'EXPENSE' ? 'rotate(180deg)' : 'none',
        }}
      >
        <DirectionIcon />
      </Box>
      <Box component="span" sx={{ minWidth: 0 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 500 }}>
          {t(`category.kind.${kind}`)}
        </Typography>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: 11, lineHeight: 1.4, color: tokens.text2 }}
        >
          {t(`virtualAccount.addCategory.note.${kind}`)}
        </Typography>
      </Box>
    </Box>
  );
}

interface AddCategoryFormProps {
  virtualAccountId: number;
  titleId: string;
  subtitleId: string;
  draft: CategoryDraft;
  onDraft: (draft: CategoryDraft) => void;
  onBusy: (busy: boolean) => void;
  onCancel: () => void;
  onAdded: (category: Category) => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

/** The C8 form: a title, the fields and the actions, in whichever dialog hosts it. */
export function AddCategoryForm({
  virtualAccountId,
  titleId,
  subtitleId,
  draft,
  onDraft,
  onBusy,
  onCancel,
  onAdded,
  onSubscriptionRequired,
}: AddCategoryFormProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const radioName = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = draft.name.trim();
  const set = (patch: Partial<CategoryDraft>) => onDraft({ ...draft, ...patch });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.length === 0 || submitting) return;
    setSubmitting(true);
    onBusy(true);
    setError(null);
    try {
      const description = draft.description.trim();
      const created = await categoriesApi.create(virtualAccountId, {
        name,
        kind: draft.kind,
        ...(description.length > 0 ? { description } : {}),
        taxReportable: draft.taxReportable,
      });
      await onAdded(created);
    } catch (caught) {
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) onSubscriptionRequired();
      else if (code === 'CATEGORY_NAME_TAKEN') {
        setError(t('virtualAccount.categories.exists', { name }));
        nameRef.current?.focus();
      } else setError(t('virtualAccount.errorGeneric'));
    } finally {
      setSubmitting(false);
      onBusy(false);
    }
  }

  const label = (text: string) => (
    <Typography
      component="p"
      sx={{
        mb: 1,
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: tokens.textLabel('surfaceRaised'),
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      component="form"
      onSubmit={(event: FormEvent<HTMLFormElement>) => void handleSubmit(event)}
      noValidate
    >
      <DialogTitle id={titleId}>{t('virtualAccount.addCategory.title')}</DialogTitle>
      <DialogSubtitle id={subtitleId}>{t('virtualAccount.addCategory.sub')}</DialogSubtitle>
      <DialogContent>
        <Stack spacing={1.75} sx={{ mt: 1 }}>
          <Box role="radiogroup" aria-label={t('virtualAccount.addCategory.direction')}>
            {label(t('virtualAccount.addCategory.direction'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {KINDS.map((kind) => (
                <DirectionCard
                  key={kind}
                  kind={kind}
                  name={radioName}
                  checked={draft.kind === kind}
                  onSelect={() => set({ kind })}
                />
              ))}
            </Box>
          </Box>
          <TextField
            label={t('virtualAccount.addCategory.name')}
            placeholder={t('virtualAccount.addCategory.namePlaceholder')}
            value={draft.name}
            onChange={(event) => set({ name: event.target.value })}
            inputRef={nameRef}
            required
            fullWidth
            error={error !== null}
            helperText={error ?? undefined}
            slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
          />
          <TextField
            label={t('virtualAccount.addCategory.description')}
            placeholder={t('virtualAccount.addCategory.descriptionPlaceholder')}
            value={draft.description}
            onChange={(event) => set({ description: event.target.value })}
            fullWidth
            slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH } }}
          />
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mx: 0 }}
            control={
              <Checkbox
                checked={draft.taxReportable}
                onChange={(event) => set({ taxReportable: event.target.checked })}
                sx={{ p: 0, mt: '1px', marginInlineEnd: 1.25 }}
              />
            }
            label={
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {t(`virtualAccount.tax.${draft.kind}`)}
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised') }}
                >
                  {t(`virtualAccount.addCategory.taxNote.${draft.kind}`)}
                </Typography>
              </Box>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <SubmitButton type="submit" disabled={name.length === 0} loading={submitting}>
          {t('virtualAccount.addCategory.add')}
        </SubmitButton>
      </DialogActions>
    </Box>
  );
}

interface AddCategoryDialogProps {
  virtualAccountId: number;
  onClose: () => void;
  onAdded: (category: Category) => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

export function AddCategoryDialog({
  virtualAccountId,
  onClose,
  onAdded,
  onSubscriptionRequired,
}: AddCategoryDialogProps) {
  const titleId = useId();
  const subtitleId = useId();
  const fullScreen = useDialogFullScreen();
  const [draft, setDraft] = useState<CategoryDraft>(EMPTY_CATEGORY_DRAFT);
  const [busy, setBusy] = useState(false);
  const guard = useDialogDirtyGuard(isCategoryDraftDirty(draft), onClose, busy);
  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitleId={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xs"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 460 } }}
    >
      <AddCategoryForm
        virtualAccountId={virtualAccountId}
        titleId={titleId}
        subtitleId={subtitleId}
        draft={draft}
        onDraft={setDraft}
        onBusy={setBusy}
        onCancel={guard.requestClose}
        onAdded={async (category) => {
          await onAdded(category);
          onClose();
        }}
        onSubscriptionRequired={() => {
          onClose();
          onSubscriptionRequired();
        }}
      />
    </GuardedDialog>
  );
}
